import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { ProjectConstant } from '../../interfaces/project-constants.interface';
import { GeneralHelpers } from '../../helpers/general.helper';
import {
  EDIT_PROJECT_CONSTANT,
  RELOAD_PROJECT_CONSTANTS,
} from '../../constants/general.constants';
import { ProjectService } from '../project/project.service';
import { MessageService } from '../message/message.service';
import { AppEventBus } from '../events/app-events';
import { VersionService } from '../version/version.service';
import { Subscription } from 'rxjs';
// ─────────────────────────────────────────────────────────────────────────────
// Localforage
import { Version, VersionUpdate } from '../../interfaces/project.interface';
import { AppStateService } from '../app-state/app-state.service';
import { PermissionId } from '../../constants/permissions.constants';

@Injectable({
  providedIn: 'root',
})
export class ProjectVariablesService {
  private projectService = inject(ProjectService);
  private messageService = inject(MessageService);
  private appEventBus = inject(AppEventBus);
  private versionService = inject(VersionService);

  #appState = inject(AppStateService);
  #projectService = inject(ProjectService);

  // Backward compatibility - keep original array
  public variableList: ProjectConstant[] | any = [];

  // Signal-based reactive list and count for UI reactivity
  readonly #variableListSignal = signal<ProjectConstant[]>([]);
  readonly variableList$ = this.#variableListSignal.asReadonly();
  variableListCount = computed(() => this.#variableListSignal().length);

  // Helper method to update both array and signal
  private updateVariableList(value: ProjectConstant[] | any): void {
    if (Array.isArray(value)) {
      this.variableList = value;
      this.#variableListSignal.set(value);
    } else {
      this.variableList = [];
      this.#variableListSignal.set([]);
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Permission check
  private hasWritePermission = computed(() =>
    this.#projectService.getCurrentUserHasProjectPermission(PermissionId.WRITE)
  );

  // ─────────────────────────────────────────────────────────────────────
  // Message subscription
  private messageSubscription!: Subscription;

  constructor() {
    // Reload constants whenever the active version changes (initial load,
    // version switch, head <-> non-head). getProjectConstantsList() reads
    // #appState.version() internally, so the effect tracks that signal and
    // re-runs on change. A constructor-only call ran before the version had
    // loaded, leaving variableList empty forever (SCRUM-741).
    effect(() => {
      this.getProjectConstantsList();
    });
    this.messageSubscription = this.messageService
      .getMessage()
      .subscribe((message: any) => {
        if (message && message.text === RELOAD_PROJECT_CONSTANTS) {
          this.getProjectConstantsList();
        }
      });
    // SCRUM-751: server-driven live refresh. A project_constants change on the
    // watched project arrives as the typed `reloadProjectConstants` event.
    // Unlike the local RELOAD_PROJECT_CONSTANTS message (fired in contexts where
    // #appState.version() is already fresh), a remote edit leaves the cached
    // version stale — getProjectConstantsList() only re-parses
    // #appState.version().defaultState. Do a TARGETED refetch of just the
    // current version's constants and update the list directly. Deliberately
    // NOT refreshProjectData(), which refetches the whole project and cascades
    // other reloads (we must not full-refresh on a notification).
    // Singleton service, so this lives for the app lifetime like the above.
    this.appEventBus.on('reloadProjectConstants').subscribe(() => {
      this.refreshConstantsFromServer();
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Set Project Constants List

  private setProjectConstantsList(
    variableList: ProjectConstant[],
    projectId: string | any = this.#appState.projectId().toString()
  ) {
    if (Array.isArray(variableList)) {
      const versionId = this.#appState.versionId();

      if (!versionId) {
        console.warn('No version ID available for saving project constants');
        return;
      }

      if (!this.#appState.isHeadVersion() || !this.hasWritePermission()) {
        console.warn('No permission to save project constants or not on head version');
        return;
      }

      const defaultState = GeneralHelpers.jsonStringify(variableList);

      const selectedProject = this.#appState.project();

      if (!selectedProject) {
        console.warn('No selected project available for saving constants');
        return;
      }

      this.projectService
        .updateProjectVersion(Number(projectId), versionId, {
          defaultState: defaultState,
          name: selectedProject.name,
          description: selectedProject.description,
        } as VersionUpdate)
        .subscribe({
          next: (res: Version) => {
            if (res.defaultState && GeneralHelpers.isJSON(res.defaultState)) {
              this.updateVariableList(GeneralHelpers.jsonParse(res.defaultState));
            }
          },
          error: (error) => {
            console.error('Error saving project constants:', error);
          },
        });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Get Project Constants List

  // SCRUM-751: targeted refetch of the current version's constants for the
  // server-driven live-refresh seam. Fetches ONLY the active version (no full
  // project reload, no cascading refreshes) and updates the list directly.
  private refreshConstantsFromServer() {
    const projectId = this.#appState.projectId();
    const versionId = this.#appState.versionId();
    if (!projectId || !versionId) {
      return;
    }
    this.versionService.getProjectVersion(projectId, versionId).subscribe({
      next: (version) => {
        const defaultState = version?.defaultState;
        if (defaultState && GeneralHelpers.isJSON(defaultState)) {
          this.updateVariableList(GeneralHelpers.jsonParse(defaultState));
        } else {
          this.updateVariableList([]);
        }
      },
      error: (error) => {
        console.warn('Failed to refresh project constants from server:', error);
      },
    });
  }

  private getProjectConstantsList() {
    const version = this.#appState.version();

    // Version not loaded yet: leave the list untouched (avoids clobbering on
    // the initial effect run before #appState is ready).
    if (!version) {
      return;
    }

    if (version.defaultState && GeneralHelpers.isJSON(version.defaultState)) {
      this.updateVariableList(GeneralHelpers.jsonParse(version.defaultState));
      return;
    }

    // Version loaded but has no (valid) constants — clear any stale entries
    // carried over from a previously selected version.
    this.updateVariableList([]);
  }

  public clean() {
    this.updateVariableList([]);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Add

  public add(projectConstants: ProjectConstant): ProjectConstant[] {
    this.variableList.push(projectConstants);
    this.#variableListSignal.set([...this.variableList]); // Sync signal
    this.setProjectConstantsList(this.variableList);
    return this.variableList;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Update

  public update(id: string, data: ProjectConstant): ProjectConstant[] {
    const index = this.getProjectConstantsIndex(id);
    if (index > -1) {
      this.variableList[index] = data;
      this.#variableListSignal.set([...this.variableList]);
      this.setProjectConstantsList(this.variableList);
    } else {
      console.warn(`Project constant with id ${id} not found for update`);
    }

    return this.variableList;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Delete

  public delete(id: string): ProjectConstant[] {
    const index = this.getProjectConstantsIndex(id);
    if (index > -1) {
      this.variableList.splice(index, 1);
      this.#variableListSignal.set([...this.variableList]);
      this.setProjectConstantsList(this.variableList);
    } else {
      console.warn(`Project constant with id ${id} not found for deletion`);
    }
    return this.variableList;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Edit

  public edit(id: string) {
    const index = this.getProjectConstantsIndex(id);
    if (index > -1) {
      this.messageService.sendMessage(EDIT_PROJECT_CONSTANT, {
        variable: this.variableList[index],
        index: index,
      });
    } else {
      console.warn(`Project constant with id ${id} not found for editing`);
    }
  }

  private getProjectConstantsIndex(id: string): number {
    const index = this.variableList.findIndex(
      (item: ProjectConstant) => item.id === id
    );
    return index;
  }
}
